import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Radio, X, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const TYPE_LABEL_KEYS = {
  security: "emergency.security",
  fire: "emergency.fire",
  medical: "emergency.medical",
  accident: "emergency.accident",
  banditry: "emergency.banditry",
  ecaution: "emergency.ecaution",
  location_share: "live_now.location",
};

/**
 * LiveNowBanner — pinned strip above the dashboard that lights up whenever
 * one or more alerts have an active video room. Tapping "Join" deep-links
 * the dashboard to the corresponding alert, which scrolls its card into
 * view and exposes the per-card LiveRoomLauncher (where the actual Jitsi
 * iframe mounts).
 *
 * The user may dismiss the banner for the current session — a new live
 * room appearing afterwards will re-show it automatically (we key the
 * "dismissed" set against the live alert ids that triggered the dismiss,
 * so a NEW room with a different id wins back attention).
 */
export default function LiveNowBanner({ alerts }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const liveAlerts = useMemo(
    () => (alerts || []).filter((a) => a.live_room_active && a.status !== "resolved"),
    [alerts],
  );

  // Dismiss is keyed against the exact set of currently-live alert ids; if
  // that set changes (a new room opens, or all close and re-open later),
  // the banner returns.
  const liveKey = useMemo(
    () => liveAlerts.map((a) => a.alert_id).sort().join("|"),
    [liveAlerts],
  );
  const [dismissedKey, setDismissedKey] = useState(null);
  useEffect(() => {
    // Reset dismissal when the live set changes
    if (liveKey !== dismissedKey) {
      // no-op; we don't auto-clear here — clearing happens on dismiss click
    }
  }, [liveKey, dismissedKey]);

  if (liveAlerts.length === 0) return null;
  if (dismissedKey === liveKey) return null;

  const primary = liveAlerts[0];
  const extras = liveAlerts.slice(1);

  const goToAlert = (id) => navigate(`/dashboard?alert=${encodeURIComponent(id)}`);

  return (
    <div
      className="sticky top-0 z-30 bg-red-600 text-white shadow-md"
      data-testid="live-now-banner"
      role="region"
      aria-label={t("live_now.aria_region")}
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center gap-3">
        {/* Pulsing LIVE badge */}
        <div className="flex items-center gap-2 shrink-0" aria-hidden>
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
          </span>
          <Radio className="w-4 h-4" />
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase">
            {t("live_now.label")}
          </span>
        </div>

        {/* Primary alert summary (truncates on small screens) */}
        <div className="min-w-0 flex-1 flex items-baseline gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 rounded-sm px-1.5 py-0.5">
            {TYPE_LABEL_KEYS[primary.alert_type] ? t(TYPE_LABEL_KEYS[primary.alert_type]) : primary.alert_type}
          </span>
          <p className="text-sm font-body truncate">
            <span className="font-semibold">
              {primary.description || t("live_now.default_desc")}
            </span>
            {primary.address && (
              <span className="text-white/75 ml-2 font-normal">
                · {primary.address}
              </span>
            )}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => goToAlert(primary.alert_id)}
            className="bg-white text-red-700 hover:bg-red-50 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm transition-colors"
            data-testid="live-now-join-primary"
          >
            {t("live_now.join")}
          </button>

          {extras.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest bg-white/20 hover:bg-white/30 rounded-sm px-2 py-1.5 transition-colors"
                data-testid="live-now-extras-trigger"
                aria-label={t("live_now.aria_more_rooms", { n: extras.length })}
              >
                {t("live_now.more", { n: extras.length })} <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-72 rounded-sm"
                data-testid="live-now-extras"
              >
                <div className="px-3 py-2 border-b border-gray-200">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">
                    {t("live_now.all_rooms", { n: liveAlerts.length })}
                  </p>
                </div>
                {liveAlerts.map((a) => (
                  <DropdownMenuItem
                    key={a.alert_id}
                    onClick={() => goToAlert(a.alert_id)}
                    className="px-3 py-2 cursor-pointer rounded-none focus:bg-red-50"
                    data-testid={`live-now-item-${a.alert_id}`}
                  >
                    <div className="flex flex-col gap-0.5 w-full min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-bold uppercase tracking-widest bg-red-100 text-red-700 rounded-sm px-1.5">
                          {TYPE_LABEL_KEYS[a.alert_type] ? t(TYPE_LABEL_KEYS[a.alert_type]) : a.alert_type}
                        </span>
                        <span className="text-xs font-semibold truncate">
                          {a.description || t("live_now.default_short")}
                        </span>
                      </div>
                      {a.address && (
                        <span className="text-[10px] text-gray-500 truncate">{a.address}</span>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <button
            type="button"
            onClick={() => setDismissedKey(liveKey)}
            className="text-white/80 hover:text-white p-1 rounded-sm hover:bg-white/10 transition-colors"
            aria-label={t("live_now.dismiss")}
            data-testid="live-now-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
